/**
 * =============================================================================
 * SPEQ55 RFP Pro - Public Layout Component
 * =============================================================================
 *
 * Layout wrapper for public marketing pages (Home, About, Pricing, Contact).
 * Includes navigation bar and footer.
 *
 * @module components/layout/PublicLayout
 * @author SPEQ55
 */

import { useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import speq55Logo from '../../assets/speq55-logo.png';

/**
 * Navigation links for the public site.
 */
const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'About', path: '/about' },
  { name: 'Pricing', path: '/pricing' },
  { name: 'Contact', path: '/contact' },
];

/**
 * Public layout component with navigation and footer.
 *
 * @returns {JSX.Element} Public layout
 */
function PublicLayout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  /**
   * Check if a nav link is active.
   */
  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen flex flex-col">
      {/* Navigation Bar */}
      <nav className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            {/* Logo */}
            <div className="flex items-center">
              <Link to="/" className="flex items-center space-x-2">
                <img src={speq55Logo} alt="SPEQ55" className="h-10 w-auto" />
                <span className="text-xl font-bold text-primary-600">RFP Pro</span>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex md:items-center md:space-x-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`
                    text-sm font-medium transition-colors duration-200
                    ${
                      isActive(link.path)
                        ? 'text-primary-600'
                        : 'text-accent-600 hover:text-primary-600'
                    }
                  `}
                >
                  {link.name}
                </Link>
              ))}
              <Link
                to="/login"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 transition-colors duration-200"
              >
                Sign In
              </Link>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center">
              <button
                type="button"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="inline-flex items-center justify-center p-2 rounded-md text-accent-500 hover:text-accent-700 hover:bg-accent-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
                aria-expanded={mobileMenuOpen}
              >
                <span className="sr-only">Open main menu</span>
                {mobileMenuOpen ? (
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                ) : (
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-accent-200">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`
                    block px-3 py-2 rounded-md text-base font-medium
                    ${
                      isActive(link.path)
                        ? 'bg-primary-50 text-primary-600'
                        : 'text-accent-600 hover:bg-accent-50 hover:text-primary-600'
                    }
                  `}
                >
                  {link.name}
                </Link>
              ))}
              <Link
                to="/login"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-3 py-2 rounded-md text-base font-medium text-white bg-primary-600 hover:bg-primary-700 text-center mt-2"
              >
                Sign In
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="flex-grow">
        <Outlet />
      </main>

      {/* =========================================================================
          FOOTER
          =========================================================================
          Site footer with brand info, navigation links, and legal links.

          COLOR SCHEME:
          - Background: secondary-950 (very dark green) - represents environmental focus
          - Text: white (default) - high contrast for readability
          - Links hover: secondary-300 (light green) - maintains green theme
          - Links active: primary-200 (light blue) - provides click feedback

          LAYOUT:
          - 4-column grid on desktop (brand takes 2 columns)
          - Single column stack on mobile
          - Bottom bar with copyright separated by border
      */}
      <footer className="bg-secondary-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Brand Section - Takes 2 columns on desktop */}
            <div className="col-span-1 md:col-span-2">
              {/* Logo and product name */}
              <div className="flex items-center space-x-2 mb-4">
                <img src={speq55Logo} alt="SPEQ55" className="h-8 w-auto" />
                <span className="text-lg font-bold">RFP Pro</span>
              </div>
              {/* Product tagline/description */}
              <p className="text-white text-sm max-w-md">
                Transform your RFP response process with AI-powered analysis. Extract key
                requirements, identify opportunities, and win more business.
              </p>
            </div>

            {/* Quick Links - Navigation shortcuts */}
            <div>
              <h3 className="text-white text-sm font-semibold uppercase tracking-wider mb-4">
                Quick Links
              </h3>
              <ul className="space-y-2">
                {navLinks.map((link) => (
                  <li key={link.path}>
                    {/*
                      Link color states:
                      - Default: white
                      - Hover: light green (secondary-300)
                      - Active/Click: light blue (primary-200)
                    */}
                    <Link
                      to={link.path}
                      className="text-white hover:text-secondary-300 active:text-primary-200 text-sm transition-colors duration-200"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Legal Links - Privacy and Terms */}
            <div>
              <h3 className="text-white text-sm font-semibold uppercase tracking-wider mb-4">
                Legal
              </h3>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/privacy"
                    className="text-white hover:text-secondary-300 active:text-primary-200 text-sm transition-colors duration-200"
                  >
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link
                    to="/terms"
                    className="text-white hover:text-secondary-300 active:text-primary-200 text-sm transition-colors duration-200"
                  >
                    Terms of Service
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar - Copyright notice */}
          <div className="mt-8 pt-8 border-t border-secondary-800">
            <p className="text-white text-sm text-center">
              &copy; {new Date().getFullYear()} SPEQ55. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default PublicLayout;
